import React, { useEffect, useState } from 'react';
import { useRotatingControls } from '../shared';
import { MoleculeSpinner } from '../../shared/MoleculeSpinner';

interface VideoReadyProps {
  prompt: string;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  isReady: boolean;
  error: string | null;
  facingMode: 'user' | 'environment';
  isRecording: boolean;
  isPaused: boolean;
  duration: number;
  onStartCamera: () => void;
  onFlip: () => void;
  onRecord: () => void;
  onStop: () => void;
  onPause: () => void;
  onResume: () => void;
  onClose: () => void;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const VideoReady: React.FC<VideoReadyProps> = ({
  prompt,
  videoRef,
  canvasRef,
  isReady,
  error,
  facingMode,
  isRecording,
  isPaused,
  duration,
  onStartCamera,
  onFlip,
  onRecord,
  onStop,
  onPause,
  onResume,
  onClose,
}) => {
  const [showManualStart, setShowManualStart] = useState(false);
  const { buttonStyle } = useRotatingControls();

  // Defer camera start to next frame so video ref is attached after first paint
  useEffect(() => {
    const raf = requestAnimationFrame(() => onStartCamera());
    return () => cancelAnimationFrame(raf);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isReady && !error) {
      const t = setTimeout(() => setShowManualStart(true), 3000);
      return () => clearTimeout(t);
    }
    setShowManualStart(false);
  }, [isReady, error]);

  return (
    <div className="vc-screen">
      {/* Hidden canvas for orientation-corrected recording */}
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
      />

      {/* Full-screen viewfinder */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        disablePictureInPicture
        className="vc-viewfinder-video"
        style={{
          transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
        }}
      />

      {/* Loading overlay */}
      {!isReady && !error && (
        <div className="vc-loading">
          <MoleculeSpinner />
          <span>Starting camera…</span>
          {showManualStart && (
            <button className="vc-manual-start" onClick={onStartCamera}>
              Tap to start camera
            </button>
          )}
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="vc-error">
          <span>Camera not available</span>
          <button onClick={onStartCamera}>Retry</button>
        </div>
      )}

      {/* Header: close + full prompt */}
      <div className="vc-header">
        <button className="vc-close-btn" onClick={onClose} aria-label="Close camera">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" style={buttonStyle}>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <p className="vc-prompt">{prompt}</p>
      </div>

      {/* Recording indicator */}
      {isRecording && (
        <div className={`vc-rec-indicator${duration >= 5 && !isPaused ? ' vc-rec-indicator--good' : ''}`}>
          <span className="vc-rec-indicator-inner" style={buttonStyle}>
            <span className={`vc-rec-indicator-dot${isPaused ? ' vc-rec-indicator-dot--paused' : ''}`} />
            <span className="vc-rec-indicator-time">{formatTime(duration)}</span>
          </span>
        </div>
      )}

      {/* Bottom controls */}
      <div className="vc-bottom-controls">
        {/* Left: flip camera (ready) or spacer (recording) */}
        {!isRecording ? (
          <button className="vc-ctrl-circle" onClick={onFlip} disabled={!isReady} aria-label="Flip camera">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" style={buttonStyle}>
              <polyline points="1,4 1,10 7,10" />
              <path d="M3.51 15a9 9 0 1 0 .49-4.95" />
            </svg>
          </button>
        ) : (
          <div className="vc-ctrl-spacer" />
        )}

        {/* Center: record or stop */}
        {!isRecording ? (
          <button
            className="vc-record-ring"
            onClick={onRecord}
            disabled={!isReady}
            aria-label="Start recording"
          >
            <div className="vc-record-fill" />
          </button>
        ) : (
          <button className="vc-record-ring vc-record-ring--active" onClick={onStop} aria-label="Stop recording">
            <div className="vc-stop-fill" />
          </button>
        )}

        {/* Right: pause/resume (recording) or spacer (ready) */}
        {isRecording ? (
          <button
            className="vc-ctrl-circle"
            onClick={isPaused ? onResume : onPause}
            aria-label={isPaused ? 'Resume recording' : 'Pause recording'}
          >
            {isPaused ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={buttonStyle}>
                <path d="M8 5v14l11-7L8 5z" fill="white" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={buttonStyle}>
                <rect x="6" y="4" width="4" height="16" rx="1" fill="white" />
                <rect x="14" y="4" width="4" height="16" rx="1" fill="white" />
              </svg>
            )}
          </button>
        ) : (
          <div className="vc-ctrl-spacer" />
        )}
      </div>
    </div>
  );
};
